import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
// Using API types from @src/types/api-types/ for all ServiceStore entities
import type {
  Account,
  Contact,
  Opportunity,
  Bid,
  PurchaseOrder,
  Job
} from "types/common/ServiceStoreTypes";
import ServiceStore from "stores/ServiceStore";

/**
 * ViewStore for managing account dashboard view state and UI logic
 * Follows ViewStore pattern: stores only UI state and entity IDs, uses computed properties for entities
 */
export class AccountDashboardStore {
  rootStore: RootStore;
  // Observables - Only UI state and entity IDs
  loading = false;
  error: string | null = null;
  activeTab = 0;

  // Constructor
  constructor(rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false });
    this.rootStore = rootStore;
  }

  // Actions - Only for UI state
  /**
   * Set the active tab index
   * @param tabIndex - The tab index to set as active
   */
  setActiveTab(tabIndex: number) {
    this.activeTab = tabIndex;
  }

  /**
   * Clear all account data and reset state
   */
  clearAccount() {
    this.error = null;
    this.activeTab = 0;
  }

  /**
   * Set loading state
   * @param loading - Loading state
   */
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  /**
   * Set error state
   * @param error - Error message or null to clear
   */
  setError(error: string | null) {
    this.error = error;
  }

  // Async Actions
  /**
   * Load account details and related data
   * @param accountId - The ID of the account to load
   */
  async loadAccount(accountId: string | number) {
    // Loading account details
    this.setLoading(true);
    this.setError(null);

    try {
      // Load account and related data using ServiceStores
      await Promise.all([
        this.accountsStore.get(Number(accountId)),
        this.loadRelatedData(),
      ]);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load account";
      console.error("Failed to load account details:", { accountId, error });
      runInAction(() => {
        this.setError(errorMessage);
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  /**
   * Load related data for the account
   * @param accountId - The ID of the account to load related data for
   */
  private async loadRelatedData() {
    await Promise.all([
      this.contactsStore.find(),
      this.bidsStore.find(),
      this.jobStore.find(),
      this.purchaseOrdersStore.find(),
    ]);
  }

  // Computed values - Access entities from ServiceStores
  /**
   * Service store accessor for accounts
   */
  get accountsStore() {
    return this.rootStore.accountsStore as ServiceStore;
  }

  /**
   * Service store accessor for contacts
   */
  get contactsStore() {
    return this.rootStore.contactsStore as ServiceStore;
  }

  /**
   * Service store accessor for bids
   */
  get bidsStore() {
    return this.rootStore.bidsStore as ServiceStore;
  }

  /**
   * Service store accessor for opportunities
   */
  get opportunitiesStore() {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  /**
   * Service store accessor for purchase orders
   */
  get purchaseOrdersStore() {
    return this.rootStore.purchaseOrdersStore as ServiceStore;
  }

  /**
   * Service store accessor for jobs
   */
  get jobStore() {
    return this.rootStore.jobStore as ServiceStore;
  }

  get activeAccountId(): string | null {
    const account =
      this.rootStore.routerStore?.params?.id || this.dialogAccount;
    // Active account ID retrieved
    return account ? account.toString() : null;
  }

  /**
   * Get the current account entity from ServiceStore
   */
  get account(): Account | null {
    // Getting account from store
    if (!this.activeAccountId) return null;
    // const account = this.accountsStore.getById(this.activeAccountId) as Account || null;
    const account =
      (this.accountsStore.entities.find(
        (acc) =>
          ((acc as unknown as Account).accountId?.toString() ===
          this.activeAccountId)
      ) as unknown as Account) || null;

    // Account retrieved from store
    return account;
  }

  /**
   * Current account - prioritizes active account over dialog account
   */
  get currentAccount(): Account | null {
    // Getting current account
    return this.account || this.dialogAccount || null;
  }

  /**
   * Get contacts for the current account from ServiceStore
   */
  get contacts(): Contact[] {
    if (!this.activeAccountId) return [];
    // Filtering contacts for current account
    return this.contactsStore.entities.filter(
      (contact) =>
        ((contact as unknown as Contact).accountId?.toString() ===
        this.activeAccountId)
    ) as unknown as Contact[];
  }

  /**
   * Get bids for the current account from ServiceStore
   * Bids are related to accounts through opportunities
   */
  get bids(): Bid[] {
    if (!this.activeAccountId) return [];

    // Get opportunities for this account first
    const accountOpportunities = this.opportunitiesStore.entities.filter(
      (opportunity) =>
        ((opportunity as unknown as Opportunity).accountId?.toString() ===
        this.activeAccountId)
    );

    // Get opportunity IDs
    const opportunityIds = accountOpportunities.map(
      (opp) => (opp as unknown as Opportunity).opportunityId
    );

    // Filter bids by opportunity IDs
    return this.bidsStore.entities.filter(
      (bid) =>
        (bid as unknown as Bid).opportunityId &&
        opportunityIds.includes((bid as unknown as Bid).opportunityId)
    ) as unknown as Bid[];
  }

  /**
   * Get projects for the current account from ServiceStore
   */
  get projects(): Job[] {
    if (!this.activeAccountId) return [];
    return this.jobStore.entities
      .filter(
        (job) =>
          ((job as unknown as Job).accountId?.toString() === this.activeAccountId)
      )
      .sort(
        (a, b) =>
          ((a as unknown as Job).jobNumber || 0) - ((b as unknown as Job).jobNumber || 0)
      ) as unknown as Job[];
  }

  /**
   * Get purchase orders for the current account from ServiceStore
   */
  get purchaseOrders(): PurchaseOrder[] {
    if (!this.activeAccountId) return [];
    return this.purchaseOrdersStore.entities.filter(
      (po) =>
        ((po as unknown as PurchaseOrder).accountId?.toString() ===
        this.activeAccountId)
    ) as unknown as PurchaseOrder[];
  }

  /**
   * Total value of all bids for this account
   */
  get totalBidValue(): number {
    return this.bids.reduce((sum, bid) => sum + (bid.bidAmount || 0), 0);
  }

  /**
   * List of active purchase orders (not cancelled)
   */
  get activePurchaseOrders(): PurchaseOrder[] {
    return this.purchaseOrders.filter((po) =>
      ["Draft", "Submitted", "Approved", "Received"].includes(po.status || "")
    );
  }

  /**
   * Primary contact for this account
   */
  get primaryContact(): Contact | undefined {
    return (
      this.contacts.find((contact) => contact.isPrimaryContact) ||
      this.contacts[0]
    );
  }

  /**
   * Most recent purchase orders (limited to 10)
   */
  get recentPurchaseOrders(): PurchaseOrder[] {
    return this.purchaseOrders.slice(0, 10);
  }

  /**
   * Whether the account data is currently being loaded
   */
  get isLoading(): boolean {
    return this.loading;
  }

  /**
   * Whether there is an error state
   */
  get hasError(): boolean {
    return this.error !== null;
  }

  /**
   * Whether account data is available
   */
  get hasAccount(): boolean {
    return this.account !== null;
  }

  /**
   * Number of contacts associated with this account
   */
  get contactCount(): number {
    return this.contacts.length;
  }

  /**
   * Number of bids associated with this account
   */
  get bidCount(): number {
    return this.bids.length;
  }

  /**
   * Number of purchase orders associated with this account
   */
  get purchaseOrderCount(): number {
    return this.purchaseOrders.length;
  }

  /**
   * Number of projects associated with this account
   */
  get projectCount(): number {
    return this.projects.length;
  }

  /**
   * Get the account from dialog context query parameter
   */
  get dialogAccount(): Account | undefined {
    const dialogContext =
      this.rootStore.routerStore?.queryParams?.dialogContext;
    // Getting dialog account context

    if (!dialogContext) return undefined;

    const foundAccount = this.accountsStore.getById(
      dialogContext as number
    ) as unknown as Account;
    // Found account from dialog context
    return foundAccount;
  }

  /**
   * Parent account if this account has a parent
   */
  get parentAccount(): Account | null {
    const currentAccount = this.currentAccount;
    if (!currentAccount?.parentAccountId) return null;

    return (
      (this.accountsStore.getById(
        currentAccount.parentAccountId.toString()
      ) as unknown as Account) || null
    );
  }

  /**
   * Child accounts that have this account as parent
   */
  get childAccounts(): Account[] {
    const currentAccount = this.currentAccount;
    if (!currentAccount?.accountId) return [];

    return this.accountsStore.entities.filter(
      (acc) =>
        ((acc as unknown as Account).parentAccountId === currentAccount.accountId)
    ) as unknown as Account[];
  }
}

export default AccountDashboardStore;
